import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { EditableIncome, IncomeType } from '../hooks/useIncomes';
import { incomeTypeOptions } from '../incomeTypes';
import { calculateAnnualAmount, calculateMonthlyAmount, formatCurrency } from '../utils';
import { showError } from 'src/notifications/notifications';

const frequencyOptions = [
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Annually', label: 'Annually' },
  { value: 'One-off', label: 'One-off' },
] as const;

type Frequency = (typeof frequencyOptions)[number]['value'];

type IncomeModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (income: EditableIncome) => Promise<void>;
  initialValues?: EditableIncome;
};

const defaultValues = {
  clientId: '',
  id: undefined,
  description: '',
  incomeType: 'Employment' as IncomeType,
  frequency: 'Monthly' as Frequency,
  amountPreTax: 0,
  amountPostTax: 0,
};

export function IncomeModal({ opened, onClose, onSubmit, initialValues }: IncomeModalProps) {
  const isEditing = !!initialValues?.id;
  const [frequency, setFrequency] = useState<Frequency>(
    (initialValues?.frequency as Frequency) || 'Monthly',
  );

  const form = useForm<EditableIncome>({
    initialValues: initialValues || defaultValues,
    validate: {
      amountPreTax: value =>
        typeof value !== 'number' || value < 0
          ? 'Amount before tax must be a number (0 or greater)'
          : null,
      amountPostTax: value =>
        typeof value !== 'number' || value < 0
          ? 'Amount after tax must be a number (0 or greater)'
          : null,
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (initialValues) {
      form.setValues(initialValues);
      setFrequency((initialValues.frequency as Frequency) || 'Monthly');
    } else {
      form.setValues(defaultValues);
      setFrequency('Monthly');
    }
  }, [initialValues]);

  const handleClose = () => {
    form.setValues(defaultValues);
    setFrequency('Monthly');
    onClose();
  };

  const handleSubmit = async (values: EditableIncome) => {
    try {
      await onSubmit(values);
      form.setValues(defaultValues);
      setFrequency('Monthly');
    } catch {
      showError('Failed to save income');
    }
  };

  const getMonthlyAmount = (amount: number) => {
    return calculateMonthlyAmount(amount, form.values.frequency);
  };

  const getAnnualAmount = (amount: number) => {
    return calculateAnnualAmount(amount, form.values.frequency);
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isEditing ? 'Edit income' : 'Add income'}
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)} data-testid="income-modal">
        <Stack gap="xs">
          <Select
            label="Type"
            labelProps={{ variant: 'light' }}
            data={incomeTypeOptions}
            {...form.getInputProps('incomeType')}
            required
          />
          <TextInput
            label="Description"
            labelProps={{ variant: 'light' }}
            {...form.getInputProps('description')}
          />
          <Select
            label="Frequency"
            labelProps={{ variant: 'light' }}
            data={frequencyOptions}
            {...form.getInputProps('frequency')}
            required
            onChange={value => {
              form.setFieldValue('frequency', value as Frequency);
              setFrequency((value as Frequency) || 'Monthly');
            }}
          />
          <Group grow>
            <NumberInput
              label={`Amount before tax (${frequency.toLowerCase()})`}
              labelProps={{ variant: 'light' }}
              {...form.getInputProps('amountPreTax')}
              leftSection="£"
              required
              hideControls
            />
            <NumberInput
              label={`Amount after tax (${frequency.toLowerCase()})`}
              labelProps={{ variant: 'light' }}
              {...form.getInputProps('amountPostTax')}
              leftSection="£"
              required
              hideControls
            />
          </Group>
        </Stack>

        <Box mt="xl">
          <Flex align="center" gap="md" mb="sm">
            <Text fw={500} size="xs" c="gray.4" p={0} m={0}>
              Income preview
            </Text>
            <Box flex={1} h={1} bg="gray.2" />
          </Flex>
          <Stack gap="sm">
            <Stack gap="xxxs">
              <Flex justify="space-between">
                <Text fw={600} size="md" c="gray.10">
                  Before tax{' '}
                  <Text span c="gray.4">
                    (monthly)
                  </Text>
                </Text>
                <Text fw={600} size="md" c="gray.10">
                  {formatCurrency(getMonthlyAmount(form.values.amountPreTax))}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text fw={600} size="md" c="gray.10">
                  After tax{' '}
                  <Text span c="gray.4">
                    (monthly)
                  </Text>
                </Text>
                <Text fw={600} size="md" c="gray.10">
                  {formatCurrency(getMonthlyAmount(form.values.amountPostTax))}
                </Text>
              </Flex>
            </Stack>
            <Stack gap="xxxs">
              <Flex justify="space-between">
                <Text fw={600} size="md" c="gray.10">
                  Before tax{' '}
                  <Text span c="gray.4">
                    (annually)
                  </Text>
                </Text>
                <Text fw={600} size="md" c="gray.10">
                  {formatCurrency(getAnnualAmount(form.values.amountPreTax))}
                </Text>
              </Flex>
              <Flex justify="space-between">
                <Text fw={600} size="md" c="gray.10">
                  After tax{' '}
                  <Text span c="gray.4">
                    (annually)
                  </Text>
                </Text>
                <Text fw={600} size="md" c="gray.10">
                  {formatCurrency(getAnnualAmount(form.values.amountPostTax))}
                </Text>
              </Flex>
            </Stack>
          </Stack>
        </Box>

        <Group justify="flex-end" mt="xl">
          <Button variant="filled-white" fullWidth type="submit">
            {isEditing ? 'Confirm & save' : 'Confirm & add'}
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
